import { useMutation, useQuery, useQueryClient, UseQueryResult } from "@tanstack/react-query";
import { isQueryError, QueryError, QueryResult } from "../../utils/Query";
import { queryRequest } from "../../utils/QueryAxios";
import { EntityCollection } from "../../models/DTOs";
import {
    Content,
    ContentGroup,
    ContentGroupType,
    ContentType,
    FpbSummary,
    ImageUpload,
    OfferWindowWithTenantName,
} from "../../models/vod/Models";

const useGetContentGroups =
    (params: {
        type: ContentGroupType,
        aggregatorId?: string,
        contentPartnerId?: string,
        search?: string,
        pageIndex?: number,
        pageSize?: number,
        sortField?: string,
        sortDirection?: "ASC" | "DESC",
    }): UseQueryResult<EntityCollection<ContentGroup>> =>
        useQuery({
            queryKey: [ "useGetContentGroups", params ],
            queryFn: async (): Promise<EntityCollection<ContentGroup>> => {
                const queryResult: QueryResult<EntityCollection<ContentGroup>> | QueryError<EntityCollection<ContentGroup>> =
                    await queryRequest({
                        url: "/vod/content-group",
                        params: {
                            type: params.type,
                            aggregator_id: params.aggregatorId,
                            content_partner_id: params.contentPartnerId,
                            search: params.search,
                            page_index: params.pageIndex,
                            page_size: params.pageSize,
                            sort_field: params.sortField,
                            sort_direction: params.sortDirection,
                        },
                    }) as QueryResult<EntityCollection<ContentGroup>> | QueryError<EntityCollection<ContentGroup>>;

                if (isQueryError(queryResult)) {
                    throw new Error(queryResult.statusText);
                }

                return queryResult?.data
                       ? queryResult.data
                       : {
                             pageIndex: 0,
                             requestedPageSize: 0,
                             total: 0,
                             items: [],
                         };
            },
        });

export const useGetSeriesContentGroups =
    (params: {
        aggregatorId?: string,
        contentPartnerId?: string,
        search?: string,
        pageIndex?: number,
        pageSize?: number,
        sortField?: string,
        sortDirection?: "ASC" | "DESC",
    }): UseQueryResult<EntityCollection<ContentGroup>> =>
        useGetContentGroups({
            ...params,
            type: "SERIES",
        });

export const useGetBoxSetContentGroups =
    (params: {
        aggregatorId?: string,
        contentPartnerId?: string,
        search?: string,
        pageIndex?: number,
        pageSize?: number,
        sortField?: string,
        sortDirection?: "ASC" | "DESC",
    }): UseQueryResult<EntityCollection<ContentGroup>> =>
        useGetContentGroups({
            ...params,
            type: "BOX_SET",
        });

export const useGetContentGroup =
    (amsProviderId: string, baseContentGroupId: string, enabled: boolean = true): UseQueryResult<ContentGroup | undefined> =>
        useQuery({
            queryKey: [ "useGetContentGroup", { amsProviderId, baseContentGroupId } ],
            queryFn: async (): Promise<ContentGroup | undefined> => {
                if (!amsProviderId || !baseContentGroupId) return undefined;

                const queryResult: QueryResult<ContentGroup | undefined> | QueryError<ContentGroup | undefined> =
                    await queryRequest({
                        url: "/vod/content-group/" + amsProviderId + "/" + baseContentGroupId,
                    }) as QueryResult<ContentGroup | undefined> | QueryError<ContentGroup | undefined>;

                if (isQueryError(queryResult)) {
                    throw new Error(queryResult.statusText);
                }

                return queryResult?.data
                    ? queryResult.data
                    : {};
            },
            enabled,
        });

export const useGetContentSubGroups =
    (amsProviderId: string, baseContentGroupId: string): UseQueryResult<ContentGroup[]> =>
        useQuery({
            queryKey: [ "useGetContentSubGroups", { amsProviderId, baseContentGroupId } ],
            queryFn: async (): Promise<ContentGroup[]> => {
                const queryResult: QueryResult<ContentGroup[]> | QueryError<ContentGroup[]> =
                    await queryRequest({
                        url: "/vod/content-group/" + amsProviderId + "/" + baseContentGroupId + "/content-group",
                    }) as QueryResult<ContentGroup[]> | QueryError<ContentGroup[]>;

                if (isQueryError(queryResult)) {
                    throw new Error(queryResult.statusText);
                }

                return queryResult?.data
                    ? queryResult.data
                    : [];
            },
        });

export const useGetContentGroupContent =
    (amsProviderId: string, baseContentGroupId: string): UseQueryResult<Content[]> =>
        useQuery({
            queryKey: [ "useGetContentGroupContent", { amsProviderId, baseContentGroupId } ],
            queryFn: async (): Promise<Content[]> => {
                const queryResult: QueryResult<Content[]> | QueryError<Content[]> =
                    await queryRequest({
                        url: "/vod/content-group/" + amsProviderId + "/" + baseContentGroupId + "/content",
                    }) as QueryResult<Content[]> | QueryError<Content[]>;

                if (isQueryError(queryResult)) {
                    throw new Error(queryResult.statusText);
                }

                return queryResult?.data
                    ? queryResult.data
                    : [];
            },
        });

const useGetContents =
    (params: {
        type: ContentType,
        aggregatorId?: string,
        contentPartnerId?: string,
        search?: string,
        pageIndex?: number,
        pageSize?: number,
        sortField?: string,
        sortDirection?: "ASC" | "DESC",
    }): UseQueryResult<EntityCollection<Content>> =>
        useQuery({
            queryKey: [ "useGetContents", params ],
            queryFn: async (): Promise<EntityCollection<Content>> => {
                const queryResult: QueryResult<EntityCollection<Content>> | QueryError<EntityCollection<Content>> =
                    await queryRequest({
                        url: "/vod/content",
                        params: {
                            type: params.type,
                            aggregator_id: params.aggregatorId,
                            content_partner_id: params.contentPartnerId,
                            search: params.search,
                            page_index: params.pageIndex,
                            page_size: params.pageSize,
                            sort_field: params.sortField,
                            sort_direction: params.sortDirection,
                        },
                    }) as QueryResult<EntityCollection<Content>> | QueryError<EntityCollection<Content>>;

                if (isQueryError(queryResult)) {
                    throw new Error(queryResult.statusText);
                }

                return queryResult?.data
                    ? queryResult.data
                    : {
                          pageIndex: 0,
                          requestedPageSize: 0,
                          total: 0,
                          items: [],
                      };
            },
        });

export const useGetStandaloneContents =
    (params: {
        aggregatorId?: string,
        contentPartnerId?: string,
        search?: string,
        pageIndex?: number,
        pageSize?: number,
        sortField?: string,
        sortDirection?: "ASC" | "DESC",
    }): UseQueryResult<EntityCollection<Content>> =>
        useGetContents({
            ...params,
            type: "STANDALONE",
        });

export const useGetContent =
    (amsProviderId: string, baseContentId: string, enabled: boolean = true): UseQueryResult<Content | undefined> =>
        useQuery({
            queryKey: [ "useGetContent", { amsProviderId, baseContentId } ],
            queryFn: async (): Promise<Content | undefined> => {
                if (!amsProviderId || !baseContentId) return undefined;

                const queryResult: QueryResult<Content | undefined> | QueryError<Content | undefined> =
                    await queryRequest({
                        url: "/vod/content/" + amsProviderId + "/" + baseContentId,
                    }) as QueryResult<Content | undefined> | QueryError<Content | undefined>;

                if (isQueryError(queryResult)) {
                    throw new Error(queryResult.statusText);
                }

                return queryResult?.data
                    ? queryResult.data
                    : {};
            },
            enabled,
        });

export const useEditVodContent = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (content: Content): Promise<Content | undefined> => {
            const queryResult =
                await queryRequest({
                    url: `/vod/content/edit`,
                    method: `PUT`,
                    data: content,
                })  as QueryResult<Content | undefined> | QueryError<Content | undefined>;

            if (isQueryError(queryResult)) {
                throw new Error(queryResult.statusText);
            }

            return queryResult?.data;
        },
        onSuccess: (data) => {
            if (!data || !data.id) return;

            const [ amsProviderId, baseContentId ] = data.id ? data.id.split("/") : [ "", "" ];
            queryClient.setQueryData([ "useGetContent", { amsProviderId, baseContentId } ], data);

            if (data.contentType === "EPISODE"){
                const groupId = baseContentId.substring(0, 7).padEnd(10, "0");

                queryClient.setQueryData(
                    [ "useGetContentGroupContent", { amsProviderId, baseContentGroupId: groupId } ],
                    (oldListData: ContentGroup[]) => oldListData.map(oldData => oldData.id === data.id ? data : oldData)
                );
            } else if (data.contentType === "STANDALONE"){
                queryClient.setQueriesData(
                    { queryKey: [ "useGetContents" ] },
                    (collection: EntityCollection<Content>) => {
                        if (!collection) return collection;
                        return {
                            ...collection,
                            items: collection.items.map((item) => item.id === data.id ? data : item),
                        };
                    }
                );
            }
        },
    });
};

export const useEditVodContentGroup = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (contentGroup: ContentGroup): Promise<ContentGroup | undefined> => {
            const queryResult =
                await queryRequest({
                    url: `/vod/content-group/edit`,
                    method: `PUT`,
                    data: contentGroup,
                })  as QueryResult<ContentGroup | undefined> | QueryError<ContentGroup | undefined>;

            if (isQueryError(queryResult)) {
                throw new Error(queryResult.statusText);
            }

            return queryResult?.data;
        },
        onSuccess: (data) => {
            if (!data || !data.id) return;

            const [ amsProviderId, baseContentGroupId ] = data.id ? data.id.split("/") : [ "", "" ];

            queryClient.setQueryData([ "useGetContentGroup", { amsProviderId, baseContentGroupId } ], data);

            if (data.contentGroupType === "SEASON"){
                const groupId = baseContentGroupId.substring(0, 5).padEnd(10, "0");

                queryClient.setQueryData(
                    [ "useGetContentSubGroups", { amsProviderId, baseContentGroupId: groupId } ],
                    (oldListData: ContentGroup[]) =>
                    oldListData.map(oldData => oldData.id === data.id ? data : oldData)
                );
            } else if (data.contentGroupType === "SERIES"){
                queryClient.setQueriesData(
                    { queryKey: [ "useGetContentGroups" ] },
                    (collection: EntityCollection<ContentGroup>) => {
                        if (!collection) return collection;
                        return {
                            ...collection,
                            items: collection.items.map((item) => item.id === data.id ? data : item),
                        };
                    }
                );
            }
        },
    });
};

export const useExportUpdateVodContent = () => {
    return useMutation({
        mutationFn: async ({ id, tenants }: {id: string, tenants: string[]}): Promise<Content | undefined> => {
            const queryResult =
                await queryRequest({
                    url: `/vod/vod-export/content/${id}/trigger?tenantIds=${tenants}`,
                    method: `POST`,
                })  as QueryResult<ContentGroup | undefined> | QueryError<Content | undefined>;

            if (isQueryError(queryResult)) {
                throw new Error(queryResult.statusText);
            }

            return queryResult?.data;
        },
    });
};

export const useExportUpdateVodContentGroup = () => {
    return useMutation({
        mutationFn: async ({ id, tenants }: {id: string, tenants: string[]}): Promise<ContentGroup | undefined> => {
            const queryResult =
                await queryRequest({
                    url: `/vod/vod-export/content-group/${id}/trigger?tenantIds=${tenants}`,
                    method: `POST`,
                })  as QueryResult<ContentGroup | undefined> | QueryError<Content | undefined>;

            if (isQueryError(queryResult)) {
                throw new Error(queryResult.statusText);
            }

            return queryResult?.data;
        },
    });
};

export const useGetContentMatchingContentGroupId = (id: string) => {
    return useQuery({
        queryKey: [ "useGetContentMatchingContentGroupId", id ],
        queryFn: async (): Promise<Content | undefined> => {
            const queryResult =
                await queryRequest({
                    url: `/vod/content/${id}/match`,
                })  as QueryResult<Content | undefined> | QueryError<Content | undefined>;

            if (isQueryError(queryResult)) {
                throw new Error(queryResult.statusText);
            }

            return queryResult?.data;
        },
        enabled: !!id,
    });
};

export const useGetFpbSummaries =
    (params: {
        search?: string,
        pageIndex?: number,
        pageSize?: number,
        sortField?: string,
        sortDirection?: "ASC" | "DESC",
    }): UseQueryResult<EntityCollection<FpbSummary>> =>
        useQuery({
            queryKey: [ "useGetFPBSummaries", params ],
            queryFn: async (): Promise<EntityCollection<FpbSummary>> => {
                const queryResult: QueryResult<EntityCollection<FpbSummary>> | QueryError<EntityCollection<FpbSummary>> =
                    await queryRequest({
                        url: "/vod/fpb-summary",
                        params: {
                            search: params.search,
                            page_index: params.pageIndex,
                            page_size: params.pageSize,
                            sort_field: params.sortField,
                            sort_direction: params.sortDirection,
                        },
                    }) as QueryResult<EntityCollection<FpbSummary>> | QueryError<EntityCollection<FpbSummary>>;

                if (isQueryError(queryResult)) {
                    throw new Error(queryResult.statusText);
                }

                return queryResult?.data
                    ? queryResult.data
                    : {
                        pageIndex: 0,
                        requestedPageSize: 0,
                        total: 0,
                        items: [],
                    };
            },
            placeholderData: (prev) => prev,
        });

export const useEditFpbSummary = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (fpbSummary: FpbSummary): Promise<FpbSummary[] | undefined> => {
            const formData = new FormData();
            const { certificateLocation, ...rest } = fpbSummary;

            if (certificateLocation && certificateLocation.length > 0
            && typeof certificateLocation !== "string") {
                formData.append("file", certificateLocation[0]);
            }

            formData.append(
                "fpbSummary",
                new Blob(
                    [ JSON.stringify(rest) ],
                    { type: "application/json" }
            ));

            const queryResult =
                await queryRequest({
                    url: `/vod/fpb-summary/edit`,
                    method: `PUT`,
                    data: formData,
                })  as QueryResult<FpbSummary[] | undefined> | QueryError<FpbSummary[] | undefined>;

            if (isQueryError(queryResult)) {
                throw new Error(queryResult.statusText);
            }

            return queryResult?.data;
        },
        onSuccess: (data, original) => {
            if (!data || data.length === 0) return;

            const map = new Map(
                data.map(item => [ item.assetId, item ])
            );
            queryClient.setQueriesData(
                { queryKey: [ "useGetFPBSummaries" ] },
                (collection: EntityCollection<FpbSummary>) => {
                    if (!collection) return collection;
                    return {
                        ...collection,
                        items: collection.items.map((item) => {
                            const updatedItem = map.get(item.assetId);
                            return updatedItem ? updatedItem : item;
                        }),
                    };
                }
            );

            const queryKey = [ "useGetFPBSummary", original.itemId ];
            const queryExists = !!queryClient.getQueryState(queryKey);

            if (queryExists)
                queryClient.setQueryData(queryKey, data.find(item => item.itemId === original.itemId));
        },
    });
};

export const useGetFpbSummary =
    (itemId: string): UseQueryResult<FpbSummary | undefined> =>
        useQuery({
            queryKey: [ "useGetFPBSummary", itemId ],
            queryFn: async (): Promise<FpbSummary | undefined> => {
                const queryResult: QueryResult<FpbSummary | undefined> | QueryError<FpbSummary | undefined> =
                    await queryRequest({
                        url: "/vod/fpb-summary/" + itemId,
                    }) as QueryResult<FpbSummary | undefined> | QueryError<FpbSummary | undefined>;

                if (isQueryError(queryResult)) {
                    throw new Error(queryResult.statusText);
                }

                return queryResult?.data
                    ? queryResult.data
                    : {};
            },
            enabled: !!itemId,
        });

export const useUpsertContentOfferWindows = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (content: Content): Promise<Content | undefined> => {
            const queryResult =
                await queryRequest({
                    url: `/vod/content/${content.id}/offer-window/upsert`,
                    method: `PUT`,
                    data: content.offerWindows,
                })  as QueryResult<Content | undefined> | QueryError<Content | undefined>;

            if (isQueryError(queryResult)) {
                throw new Error(queryResult.statusText);
            }

            return queryResult?.data;
        },
        onSuccess: (data) => {
            if (!data || !data.id) return;

            const [ amsProviderId, baseContentId ] = data.id ? data.id.split("/") : [ "", "" ];
            queryClient.setQueryData([ "useGetContent", { amsProviderId, baseContentId } ], data);
        },
    });
};

export const useUploadAndTransformContentImage = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ asset, assetType, file, imageType }: ImageUpload): Promise<Content | ContentGroup | undefined> => {
            const formData = new FormData();

            formData.append("file", file);
            const queryResult =
                await queryRequest({
                    url: `/vod/${assetType}/${asset.id}/provider/${asset.providerId}/content-partner/${asset.contentPartnerId}/image-type/${imageType}/upload_image`,
                    method: `POST`,
                    data: formData,
                })  as QueryResult<Content | ContentGroup | undefined> | QueryError<Content | ContentGroup | undefined>;

            if (isQueryError(queryResult)) {
                throw new Error(queryResult.statusText);
            }

            return queryResult?.data;
        },
        onSuccess: (data, imageUpload) => {
            if (!data || !data.id) return;

            const [ amsProviderId, baseAssetId ] = data.id.split("/");
            const queryKey = imageUpload.assetType === "content"
                ? [ "useGetContent", { amsProviderId, baseContentId: baseAssetId } ]
                : [ "useGetContentGroup", { amsProviderId, baseContentGroupId: baseAssetId } ];

            queryClient.setQueryData(
                queryKey,
                () => data
            );
        },
    });
};


export const useEditContentGroupPreviewVideoAssets = ()=>{
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (contentGroup: ContentGroup): Promise<ContentGroup | undefined> => {
            const queryResult =
                await queryRequest({
                    url: `/vod/content-group/${contentGroup.id}/preview-video-assets/edit`,
                    method: `PUT`,
                    data: contentGroup.previewVideoAssets,
                })  as QueryResult<ContentGroup | undefined> | QueryError<ContentGroup | undefined>;

            if (isQueryError(queryResult)) {
                throw new Error(queryResult.statusText);
            }

            return queryResult?.data;
        },
        onSuccess: (data) => {
            if (!data || !data.id) return;

            const [ amsProviderId, baseContentGroupId ] = data.id ? data.id.split("/") : [ "", "" ];

            queryClient.setQueryData([ "useGetContentGroup", { amsProviderId, baseContentGroupId } ], data);
        },
    });
};

export const useEditContentPreviewVideoAssets = ()=>{
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (content: Content): Promise<Content | undefined> => {
            const queryResult =
                await queryRequest({
                    url: `/vod/content/${content.id}/preview-video-assets/edit`,
                    method: `PUT`,
                    data: content.previewVideoAssets,
                })  as QueryResult<Content | undefined> | QueryError<Content | undefined>;

            if (isQueryError(queryResult)) {
                throw new Error(queryResult.statusText);
            }

            return queryResult?.data;
        },
        onSuccess: (data) => {
            if (!data || !data.id) return;

            const [ amsProviderId, baseContentId ] = data.id ? data.id.split("/") : [ "", "" ];

            queryClient.setQueryData([ "useGetContent", { amsProviderId, baseContentId } ], data);
        },
    });
};

export const useEditVideoAsset = ()=>{
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (content: Content): Promise<Content | undefined> => {
            const queryResult =
                await queryRequest({
                    url: `/vod/content/${content.id}/video-asset/edit`,
                    method: `PUT`,
                    data: content.videoAsset,
                })  as QueryResult<Content | undefined> | QueryError<Content | undefined>;

            if (isQueryError(queryResult)) {
                throw new Error(queryResult.statusText);
            }

            return queryResult?.data;
        },
        onSuccess: (data) => {
            if (!data || !data.id) return;

            const [ amsProviderId, baseContentId ] = data.id ? data.id.split("/") : [ "", "" ];

            queryClient.setQueryData([ "useGetContent", { amsProviderId, baseContentId } ], data);
        },
    });
};

export const useUpsertContentGroupOfferWindows = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ content, contentGroup }:{content: Content, contentGroup: ContentGroup}): Promise<ContentGroup | undefined> => {
            const queryResult =
                await queryRequest({
                    url: `/vod/content-group/${contentGroup.id}/offer-window/${contentGroup.contentGroupType}/upsert`,
                    method: `PUT`,
                    data: content.offerWindows,
                })  as QueryResult<ContentGroup | undefined> | QueryError<ContentGroup | undefined>;

            if (isQueryError(queryResult)) {
                throw new Error(queryResult.statusText);
            }

            return queryResult?.data;
        },
        onSuccess: (data) => {
            if (!data || !data.id) return;

            const [ amsProviderId, baseContentGroupId ] = data.id.split("/");

            queryClient.setQueryData([ "useGetContentGroup", { amsProviderId: amsProviderId, baseContentGroupId } ], data);
            queryClient.invalidateQueries({ queryKey: [ "useGetContentMatchingContentGroupId", data.id ] }).then();

            if (data.contentGroupType === "SEASON"){
                const id = `${baseContentGroupId.substring(0, 5)}00000`;
                queryClient.invalidateQueries({
                    queryKey: [ "useGetContentGroup", { amsProviderId: amsProviderId, baseContentGroupId: id } ],
                }).then();

            }
        },
    });
};

export const useGetVodContentIngestorProperties =
    () =>
    useQuery({
        queryKey: [ "useGetVodContentIngestorProperties" ],
        queryFn: async (): Promise<Record<string, OfferWindowWithTenantName> | undefined> => {
            const queryResult : QueryResult<Record<string, OfferWindowWithTenantName> | undefined> | QueryError<Record<string, OfferWindowWithTenantName> | undefined> =
                await queryRequest({
                    url: "/vod/content/get-ingestors",
                }) as QueryResult<Record<string, OfferWindowWithTenantName> | undefined> | QueryError<Record<string, OfferWindowWithTenantName> | undefined>;

            if (isQueryError(queryResult)) {
                throw new Error(queryResult.statusText);
            }

            return queryResult?.data
                ? queryResult.data
                : {};
        },
    });